import DOMPurify from 'isomorphic-dompurify'
import { isNil, reject } from 'ramda'
import { FDRStatus, MpxUser, ProgramEngagementUser, UserId, UserRef } from '../types'
import { OrgUserSearchResult } from '../types/OrgUserTypes'

class BaseUserModel<T extends MpxUser | OrgUserSearchResult | ProgramEngagementUser | UserRef> {
  user: T
  constructor(user: T) {
    this.user = user
  }
  matchUserId(userId: UserId) {
    return userId === this.user.userId
  }
}

class ExtendedUserModel<T extends MpxUser | OrgUserSearchResult> extends BaseUserModel<T> {
  matchUserId(userId: UserId) {
    return userId === this.user.userId
  }

  isGrower() {
    return this.user.persona === 'grower'
  }
  isAdmin() {
    return this.user.persona === 'admin'
  }
  isAdvisor() {
    return this.user.persona === 'advisor'
  }
}

export class UserRefModel extends BaseUserModel<UserRef> {
  get displayNameFull() {
    const { userPrimaryEmail, familyName, givenName } = this.user
    return DOMPurify.sanitize(
      !!givenName ? reject(isNil, [givenName, familyName]).join(' ') : userPrimaryEmail
    )
  }
  get displayInitials() {
    const { familyName, givenName } = this.user
    return !!givenName || !!familyName
      ? DOMPurify.sanitize(
          `${!!givenName?.charAt(0) ? givenName?.charAt(0) : ''}${
            familyName?.charAt(0) ? familyName?.charAt(0) : ''
          }`
        )
      : null
  }
}

export class UserModel extends ExtendedUserModel<MpxUser> {
  get displayNameFull() {
    const { username, userInfo } = this.user
    return DOMPurify.sanitize(
      !!userInfo?.givenName
        ? reject(isNil, [userInfo.givenName, userInfo.familyName]).join(' ')
        : username
    )
  }

  get displayNameGiven() {
    const { username, userInfo: { givenName } = {} } = this.user
    return DOMPurify.sanitize(!!givenName ? givenName : username)
  }

  get displayInitials() {
    const { userInfo: { givenName, familyName } = {} } = this.user
    return !!givenName || !!familyName
      ? DOMPurify.sanitize(
          `${!!givenName?.charAt(0) ? givenName?.charAt(0) : ''}${
            familyName?.charAt(0) ? familyName?.charAt(0) : ''
          }`
        )
      : null
  }

  get orgId() {
    return (
      this.user.owningOrgId ??
      this.user.userOrganization?.accountId ??
      this.user.userOrganization?.id
    )
  }
}

export class OrgUserModel extends ExtendedUserModel<OrgUserSearchResult> {
  get displayNameFull() {
    const { email, familyName, givenName } = this.user
    return DOMPurify.sanitize(
      !!givenName ? reject(isNil, [givenName, familyName]).join(' ') : email
    )
  }

  get displayNameGiven() {
    const { email, givenName } = this.user
    return !!givenName ? DOMPurify.sanitize(givenName) : DOMPurify.sanitize(email)
  }

  get displayInitials() {
    const { givenName, familyName } = this.user
    return !!givenName || !!familyName
      ? DOMPurify.sanitize(
          `${!!givenName?.charAt(0) ? givenName?.charAt(0) : ''}${
            familyName?.charAt(0) ? familyName?.charAt(0) : ''
          }`
        )
      : null
  }

  get advisor() {
    return this.user.advisor
  }
  get advisorDisplayNameFull() {
    const { email, familyName, givenName } = this.advisor || {}
    return !!givenName
      ? DOMPurify.sanitize(reject(isNil, [givenName, familyName]).join(' '))
      : email && DOMPurify.sanitize(email)
  }

  get email() {
    return DOMPurify.sanitize(this.user.email)
  }
  get userId() {
    return this.user.userId
  }

  get orgId() {
    return this.user.org?.id || 'root'
  }

  get searchResultsDisplayNameMarkdown() {
    const { matches } = this.user
    const familyName = matches?.familyName ? matches.familyName : this.user.familyName
    const givenName = matches?.givenName ? matches.givenName : this.user.givenName
    const email = this.searchResultsDisplayEmailMarkdown
    const fullName = reject(isNil, [givenName, familyName]).join(' ')
    return DOMPurify.sanitize(!!fullName ? fullName : email)
  }

  get searchResultsDisplayEmailMarkdown() {
    const { matches } = this.user
    const email = matches?.email ? matches.email : this.user.email
    return DOMPurify.sanitize(email)
  }

  get programOpportunities() {
    const programs = this.user.resources?.programs.filter(program => program.eligible)
    const opportunities = programs
      ?.sort(
        (progA, progB) =>
          (progA.rewards.potential?.usd.amount || 0) - (progB.rewards.potential?.usd.amount || 0)
      )
      .map(p => ({ programId: p.programId, fdrStatus: [FDRStatus.hasOpportunity] }))
    return opportunities
  }

  get workflows() {
    return this.user.workflows
  }
}

export class ProgramEngagementUserModel extends BaseUserModel<ProgramEngagementUser> {
  get displayNameFull() {
    const { userPrimaryEmail, familyName, givenName } = this.user
    return DOMPurify.sanitize(
      !!givenName ? reject(isNil, [givenName, familyName]).join(' ') : userPrimaryEmail
    )
  }

  get displayNameGiven() {
    const { userPrimaryEmail, givenName } = this.user
    return !!givenName ? DOMPurify.sanitize(givenName) : DOMPurify.sanitize(userPrimaryEmail)
  }

  get displayInitials() {
    const { givenName, familyName } = this.user
    return !!givenName || !!familyName
      ? DOMPurify.sanitize(
          `${!!givenName?.charAt(0) ? givenName?.charAt(0) : ''}${
            familyName?.charAt(0) ? familyName?.charAt(0) : ''
          }`
        )
      : null
  }

  get email() {
    return DOMPurify.sanitize(this.user.userPrimaryEmail)
  }
  get userId() {
    return this.user.userId
  }
}
